import { useEffect, useState } from "react";
import FormField from "../../forms/form-field";
import Topbar from "../../top-bar/top-bar";
import { BackIcon } from "../modals/icons/icons";
import { useNavigate } from "react-router-dom";
import { instance } from "../../../utils/utils";
import "../styles.css"

export default function BillingHistoryTable() {
    const navigate = useNavigate();

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [page, setPage] = useState(1);
    const [count, setCount] = useState(0);
    const [next, setNext] = useState(null);
    const [previous, setPrevious] = useState(null);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const formatCurrency = (value) => {
        return new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            minimumFractionDigits: 0
        }).format(value);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    useEffect(() => {
        async function fetchInvoices() {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                params.append("page", page);

                if (debouncedSearch) {
                    params.append("search", debouncedSearch);
                }

                const { data } = await instance.get(
                    `/customers/company/order/list?${params.toString()}`
                );

                setInvoices(data?.results?.invoices || []);
                setCount(data?.count || 0);
                setNext(data?.next);
                setPrevious(data?.previous);
            } catch (err) {
                setError("Error cargando el historial de facturación.");
            } finally {
                setLoading(false);
            }
        }

        fetchInvoices();
    }, [page, debouncedSearch]);


    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 400);

        return () => clearTimeout(handler);
    }, [search]);



    return (
        <div className="w-100">
            <Topbar />
            <div className="subscription-edit-container w-100 p-4 d-flex flex-column gap-4">
                <div className="billing-history-table-container p-4 d-flex flex-column gap-3 align-items-start">

                    <div
                        className="subscription-edit-title cursor-pointer d-flex align-items-center gap-2"
                        onClick={() => navigate("/admin/")}
                    >
                        <BackIcon />
                    </div>

                    <h5 className="billing-history-title mb-0">Factura e historial de facturación</h5>
                    <span className="billing-history-subtitle">
                        Revisa tu historial de facturación y gestiona las facturas.
                    </span>

                    <div className="billing-history-search w-50">
                        <FormField
                            type="search"
                            placeholder="Busca por número de pedido, nombre del plan, etc."
                            className="w-75"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="billing-history-table w-100">
                        {loading && (
                            <table className="table align-middle">
                                <thead className="table-light">
                                    <tr className="text-secondary">
                                        <th>Fecha</th>
                                        <th>Tipo</th>
                                        <th>Número de factura</th>
                                        <th>Planes</th>
                                        <th>Cantidad</th>
                                        <th className="text-end">Acción</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {[1, 2].map((i) => (
                                        <tr key={i}>
                                            <td>
                                                <div className="placeholder-glow">
                                                    <span className="placeholder col-6"></span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="placeholder-glow">
                                                    <span className="placeholder col-4"></span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="placeholder-glow">
                                                    <span className="placeholder col-7"></span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="placeholder-glow">
                                                    <span className="placeholder col-8"></span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="placeholder-glow">
                                                    <span className="placeholder col-5"></span>
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <div className="d-flex justify-content-end gap-2 placeholder-glow">
                                                    <span className="placeholder col-2"></span>
                                                    <span className="placeholder col-2"></span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {error && <p className="text-danger">{error}</p>}

                        {!loading && !error && (
                            <table className="table align-middle">
                                <thead className="table-light">
                                    <tr className="text-secondary">
                                        <th>Fecha</th>
                                        <th>Tipo</th>
                                        <th>Número de factura</th>
                                        <th>Planes</th>
                                        <th>Cantidad</th>
                                        <th className="text-end">Acción</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {!loading && invoices.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">
                                                {debouncedSearch
                                                    ? "No se encontraron resultados para tu búsqueda."
                                                    : "No hay facturas registradas."
                                                }
                                            </td>
                                        </tr>
                                    )}

                                    {invoices.map((inv) => (
                                        <tr key={inv.id}>
                                            <td>{formatDate(inv.created_at)}</td>
                                            <td>Factura</td>
                                            <td>{inv.invoice_number}</td>
                                            <td>{inv.plan_title || "—"}</td>
                                            <td>{formatCurrency(inv.amount)}</td>

                                            <td className="text-end d-flex justify-content-end gap-2">
                                                <button
                                                    disabled={!inv.invoice_url}
                                                    className="btn btn-outline-secondary p-2"
                                                    style={{ visibility: inv.invoice_url ? "visible" : "hidden" }}
                                                    onClick={() => inv.invoice_url && window.open(inv.invoice_url)}
                                                >
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                        <path
                                                            d="M12 5C7 5 2.73 8.11 1 12C2.73 15.89 7 19 12 19C17 19 21.27 15.89 23 12C21.27 8.11 17 5 12 5Z"
                                                            stroke="#394F65"
                                                            strokeWidth="2"
                                                        />
                                                        <circle cx="12" cy="12" r="3" stroke="#394F65" strokeWidth="2" />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {!loading && !error && invoices.length > 0 && (
                            <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                                <small className="billing-history-table-info">
                                    {invoices.length} de {count} facturas
                                </small>

                                <div className="d-flex gap-2">
                                    <button
                                        disabled={!previous}
                                        onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                        className="billing-history-table-previous"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
                                            <path d="M0.000179768 5.94987C0.000179768 5.63756 0.121539 5.32522 0.364321 5.08689L5.18234 0.357434C5.66785 -0.119145 6.45504 -0.119145 6.94056 0.357434C7.42606 0.834013 7.42606 1.60676 6.94056 2.08335L3.0016 5.94987L6.94056 9.81639C7.42606 10.293 7.42606 11.0657 6.94056 11.5423C6.45504 12.0189 5.66785 12.0189 5.18234 11.5423L0.364321 6.81285C0.121539 6.57454 0.000179768 6.26216 0.000179768 5.94987Z" fill="#9DACBD" />
                                        </svg>
                                    </button>

                                    <button
                                        disabled={!next}
                                        onClick={() => setPage(prev => prev + 1)}
                                        className="billing-history-table-next"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
                                            <path d="M7.30451 5.94857C7.30451 6.26087 7.18315 6.57322 6.94037 6.81155L2.12234 11.541C1.63684 12.0176 0.849646 12.0176 0.364128 11.541C-0.121376 11.0644 -0.121376 10.2917 0.364128 9.81509L4.30309 5.94857L0.364128 2.08205C-0.121376 1.60547 -0.121376 0.83272 0.364128 0.356118C0.849646 -0.120459 1.63684 -0.120459 2.12234 0.356118L6.94037 5.08558C7.18315 5.32389 7.30451 5.63627 7.30451 5.94857Z" fill="#9DACBD" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <span className="billing-history-table-info">
                        Solo se puede acceder al historial de facturas de los 3 años anteriores a través de la cuenta. Ponte en contacto con Atención al cliente si quieres acceder al historial de facturas anterior a ese período de tiempo.
                    </span>
                </div>
            </div>
        </div>
    );
}
